/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../context/AuthContext';
import { analyticsService } from '../../../services/AnalyticsService';
import PremiumFeatureGate from '../PremiumFeatureGate';
import {
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import {
  TrendingUpIcon,
  TrendingDownIcon,
  CurrencyDollarIcon,
  UsersIcon,
  CalendarIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  RefreshIcon,
  ChartBarIcon
} from '@heroicons/react/outline';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

const StatCard = ({ title, value, change, icon: Icon, prefix = '', suffix = '' }) => {
  const isPositive = change > 0;
  const isNeutral = change === 0;
  
  return (
    <div className="bg-white rounded-xl p-5 border border-gray-200 hover:shadow-md transition">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 mb-1">{title}</p>
          <p className="text-2xl font-bold text-gray-900">
            {prefix}{typeof value === 'number' ? value.toLocaleString() : value}{suffix}
          </p>
        </div>
        <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
          <Icon className="w-5 h-5 text-blue-600" />
        </div>
      </div>
      {change !== undefined && (
        <div className={`flex items-center gap-1 mt-3 text-sm ${
          isPositive ? 'text-green-600' : isNeutral ? 'text-gray-500' : 'text-red-600'
        }`}>
          {isPositive ? (
            <TrendingUpIcon className="w-4 h-4" />
          ) : isNeutral ? (
            <span className="w-4 h-4 flex items-center justify-center">—</span>
          ) : (
            <TrendingDownIcon className="w-4 h-4" />
          )}
          <span>{isPositive ? '+' : ''}{change}%</span>
        </div>
      )}
    </div>
  );
};

const PipelineCard = ({ pipeline }) => {
  const { t } = useTranslation();
  const stages = [
    { key: 'applied', label: 'Applied', color: 'bg-blue-500', icon: ClockIcon },
    { key: 'approved', label: 'Approved', color: 'bg-green-500', icon: CheckCircleIcon },
    { key: 'paid', label: 'Paid', color: 'bg-emerald-600', icon: CurrencyDollarIcon },
    { key: 'denied', label: 'Denied', color: 'bg-red-500', icon: XCircleIcon }
  ];

  const total = Object.values(pipeline).reduce((a, b) => a + b, 0);

  return (
    <div className="bg-white rounded-xl p-6 border border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('analytics.applicationPipeline')}</h3>
      <div className="space-y-4">
        {stages.map(stage => {
          const value = pipeline[stage.key] || 0;
          const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
          
          return (
            <div key={stage.key}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <stage.icon className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-600">{stage.label}</span>
                </div>
                <span className="text-sm font-medium text-gray-900">{value}</span>
              </div>
              <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full ${stage.color} transition-all duration-500`}
                  style={{ width: `${percentage}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-4 pt-4 border-t border-gray-100">
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">{t('analytics.totalApplications')}</span>
          <span className="font-semibold text-gray-900">{total}</span>
        </div>
      </div>
    </div>
  );
};

const AnalyticsDashboardContent = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [periodData, setPeriodData] = useState(null);
  const [timePeriod, setTimePeriod] = useState('year');

  const fetchData = useCallback(async () => {
    if (!user?.uid) return;

    try {
      const [result, periodResult] = await Promise.all([
        analyticsService.getDashboardSummary(user.uid),
        analyticsService.getRevenueByPeriod(user.uid, timePeriod)
      ]);
      if (result.success) setData(result);
      if (periodResult.success) setPeriodData(periodResult);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user?.uid, timePeriod]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const formatCurrency = (value) => {
    return `$${(value / 100).toLocaleString()}`;
  };

  const formatMonth = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <p className="text-gray-500">{t('analytics.loadingAnalytics')}</p>
      </div>
    );
  }

  if (!data || !data.summary) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-xl p-12 text-center border border-gray-200">
          <ChartBarIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('analytics.noDataYet')}</h3>
          <p className="text-gray-500 mb-6">{t('analytics.noDataDesc')}</p>
        </div>
      </div>
    );
  }

  const { summary, pipeline, yoy, categoryBreakdown, revenueTimeline } = data;

  const pieData = categoryBreakdown?.slice(0, 8).map((item, index) => ({
    name: item.category,
    value: item.count,
    color: COLORS[index % COLORS.length]
  })) || [];

  const revenueChartData = (periodData?.timeline || []).map(item => ({
    month: timePeriod === 'year'
      ? formatMonth(item.date)
      : new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    revenue: item.revenue / 100,
    transactions: item.transactions
  }));

  const periodLabels = { year: 'Year', month: 'Month', quarter: 'Quarter' };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('analytics.title')}</h1>
          <p className="text-gray-500 text-sm mt-1">{t('analytics.subtitle')}</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100/90 backdrop-blur-sm text-gray-700 rounded-lg hover:bg-gray-200 transition disabled:opacity-50"
        >
          <RefreshIcon className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? t('common.refreshing') : t('common.refresh')}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          title={t('analytics.totalRevenue')}
          value={formatCurrency(summary.totalRevenue)}
          change={yoy?.growth?.revenue}
          icon={CurrencyDollarIcon}
        />
        <StatCard
          title={t('analytics.totalEvents')}
          value={summary.totalEvents}
          icon={CalendarIcon}
        />
        <StatCard
          title={t('analytics.totalApplications')}
          value={summary.totalApplications}
          change={yoy?.growth?.transactions}
          icon={UsersIcon}
        />
        <StatCard
          title={t('analytics.approvalRate')}
          value={summary.approvalRate}
          suffix="%"
          icon={CheckCircleIcon}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">{t('analytics.revenueOverTime')}</h3>
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              {['month', 'quarter', 'year'].map(p => (
                <button
                  key={p}
                  onClick={() => setTimePeriod(p)}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition ${
                    timePeriod === p
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {periodLabels[p]}
                </button>
              ))}
            </div>
          </div>
          {revenueChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={revenueChartData}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="month" stroke="#9CA3AF" fontSize={12} />
                <YAxis stroke="#9CA3AF" fontSize={12} tickFormatter={(v) => `$${v}`} />
                <Tooltip
                  contentStyle={{ borderRadius: '8px', border: '1px solid #E5E7EB' }}
                  formatter={(value) => [`$${value.toLocaleString()}`, 'Revenue']}
                />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="#3B82F6"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorRevenue)"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-400">
              {t('analytics.noRevenueData')}
            </div>
          )}
        </div>

        <PipelineCard pipeline={pipeline || {}} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('analytics.vendorCategories')}</h3>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ borderRadius: '8px', border: '1px solid #E5E7EB' }}
                  formatter={(value, name) => [value, name]}
                />
                <Legend
                  layout="vertical"
                  align="right"
                  verticalAlign="middle"
                  iconType="circle"
                  iconSize={8}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-400">
              {t('analytics.noCategoryData')}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('analytics.yearOverYear')}</h3>
          {yoy ? (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">{t('analytics.thisYear', { year: yoy.currentYear?.year })}</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(yoy.currentYear?.revenue || 0)}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    {yoy.currentYear?.transactions || 0} {t('analytics.transactions')}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">{t('analytics.lastYear', { year: yoy.lastYear?.year })}</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(yoy.lastYear?.revenue || 0)}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    {yoy.lastYear?.transactions || 0} {t('analytics.transactions')}
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-gray-600">{t('analytics.revenueGrowth')}</span>
                    <span className={`text-sm font-semibold ${
                      yoy.growth?.revenue >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {yoy.growth?.revenue >= 0 ? '+' : ''}{yoy.growth?.revenue}%
                    </span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${yoy.growth?.revenue >= 0 ? 'bg-green-500' : 'bg-red-500'}`}
                      style={{ width: `${Math.min(Math.abs(yoy.growth?.revenue || 0), 100)}%` }}
                    />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-gray-600">{t('analytics.transactionGrowth')}</span>
                    <span className={`text-sm font-semibold ${
                      yoy.growth?.transactions >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {yoy.growth?.transactions >= 0 ? '+' : ''}{yoy.growth?.transactions}%
                    </span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${yoy.growth?.transactions >= 0 ? 'bg-green-500' : 'bg-red-500'}`}
                      style={{ width: `${Math.min(Math.abs(yoy.growth?.transactions || 0), 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-400">
              {t('analytics.noComparisonData')}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 border border-gray-200 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('analytics.keyMetrics')}</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4">
            <p className="text-3xl font-bold text-blue-600">{summary.approvedApplications}</p>
            <p className="text-sm text-gray-500 mt-1">{t('analytics.approved')}</p>
          </div>
          <div className="text-center p-4">
            <p className="text-3xl font-bold text-green-600">{summary.paidApplications}</p>
            <p className="text-sm text-gray-500 mt-1">{t('analytics.paid')}</p>
          </div>
          <div className="text-center p-4">
            <p className="text-3xl font-bold text-yellow-600">{summary.pendingApplications}</p>
            <p className="text-sm text-gray-500 mt-1">{t('analytics.pending')}</p>
          </div>
          <div className="text-center p-4">
            <p className="text-3xl font-bold text-purple-600">{summary.conversionRate}%</p>
            <p className="text-sm text-gray-500 mt-1">{t('analytics.conversionRate')}</p>
          </div>
        </div>
      </div>

      {data.topEvents?.byRevenue?.length > 0 && (
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Events</h3>
          <div className="space-y-3">
            {data.topEvents.byRevenue.map((event, index) => (
              <div key={event.eventId} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-100 text-blue-700 font-bold text-sm shrink-0">
                  {index + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{event.eventName || 'Untitled Event'}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{event.applications} applications</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-semibold text-gray-900">{formatCurrency(event.revenue)}</p>
                  {event.capacity > 0 && (
                    <p className="text-xs text-gray-500 mt-0.5">{event.fillRate}% filled</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const AnalyticsDashboard = () => {
  return (
    <PremiumFeatureGate featureName="Analytics Dashboard">
      <AnalyticsDashboardContent />
    </PremiumFeatureGate>
  );
};

export default AnalyticsDashboard;
